import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';
import DataTable from '../components/common/DataTable';
import ConfirmModal from '../components/common/ConfirmModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import '../styles/UsersPage.css';
import { getAllUsers, deleteUser, registerUser, getPendingActivations, activateUser } from '../utils/api';
import { UserPlus, Trash2, Eye, Mail, Shield, User, CheckCircle, UsersRound, Activity, ArrowUpRight } from 'lucide-react';

const UsersPage = ({ roleFilter = null }) => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const goProfile = (row) => navigate(`/admin/profile/${row.id || row.user_id}`);
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [addModal, setAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ full_name: '', email: '', phone: '', user_type: 'client' });
  const [activating, setActivating] = useState(null);

  const { data: allUsers, isLoading, execute: fetchUsers } = useApi(getAllUsers, { immediate: true });
  const { data: pending, execute: fetchPending } = useApi(getPendingActivations, { immediate: true });
  const users = roleFilter
    ? (allUsers || []).filter(u => (u.role || u.user_type) === roleFilter)
    : allUsers;

  const handleActivate = async (u) => {
    setActivating(u.user_id);
    try {
      await activateUser(u.user_id);
      success(`Conta de ${u.full_name} ativada. Já pode iniciar sessão.`);
      fetchPending();
      fetchUsers();
    } catch (err) {
      error(err.response?.data?.message || 'Erro ao ativar a conta');
    } finally {
      setActivating(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.user) return;
    try {
      await deleteUser(deleteModal.user.user_id || deleteModal.user.id);
      success('Utilizador eliminado com sucesso!');
      fetchUsers();
      setDeleteModal({ open: false, user: null });
    } catch (err) {
      error('Erro ao eliminar utilizador');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await registerUser(newUser);
      success('Utilizador criado com sucesso!');
      setAddModal(false);
      setNewUser({ full_name: '', email: '', phone: '', user_type: 'client' });
      fetchUsers();
    } catch (err) {
      error('Erro ao criar utilizador');
    }
  };

  const userTypeBadge = (type) => {
    const styles = {
      admin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      client: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      worker: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[type] || styles.client}`}>
        {type === 'admin' || type === 'super_admin' ? 'Admin' : type === 'worker' ? 'Profissional' : 'Cliente'}
      </span>
    );
  };

  const statusBadge = (status) => {
    const isActive = status === 'active' || status === true;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium
        ${isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
        {isActive ? 'Ativo' : 'Inativo'}
      </span>
    );
  };

  const columns = [
    { key: 'user_id', label: 'ID', width: '60px', render: (row) => row.user_id || row.id },
    { key: 'full_name', label: 'Nome', render: (row) => (
      <button onClick={() => goProfile(row)} className="text-white hover:text-[#00f1fe] font-medium transition-colors text-left">
        {row.full_name}
      </button>
    ) },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Tipo', render: (row) => userTypeBadge(row.role || row.user_type) },
    { key: 'status', label: 'Status', render: (row) => statusBadge(row.status || row.is_active) },
    { key: 'created_at', label: 'Registro', render: (row) => 
      row.created_at ? new Date(row.created_at).toLocaleDateString('pt-PT') : '-'
    },
    { 
      key: 'actions', 
      label: 'Ações', 
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => goProfile(row)}
            className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
            title="Ver detalhes"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button 
            onClick={() => setDeleteModal({ open: true, user: row })}
            className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
            title="Eliminar"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    },
  ];

  if (isLoading && !users) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const activeUsers = (users || []).filter(u => u.status === 'active' || u.is_active).length;
  const clientUsers = (users || []).filter(u => (u.role || u.user_type) === 'client').length;
  const activeRate = (users || []).length ? Math.round((activeUsers / users.length) * 100) : 0;

  return (
    <div className="users-admin-page space-y-6">
      <section className="users-admin-hero">
        <div>
          <span className="users-admin-eyebrow"><UsersRound className="w-4 h-4" /> Comunidade Dozy</span>
          <h1>{roleFilter === 'client' ? 'Clientes' : 'Utilizadores'}</h1>
          <p>{roleFilter === 'client' ? 'Conheça e acompanhe todos os clientes da plataforma.' : 'Administre clientes, profissionais e equipa interna num só lugar.'}</p>
        </div>
        <div className="users-admin-hero__actions">
          <div className="users-health"><Activity /><span><strong>{activeRate}%</strong> contas ativas</span></div>
          <button onClick={() => setAddModal(true)}><UserPlus className="w-4 h-4" /> Novo utilizador <ArrowUpRight className="w-4 h-4" /></button>
        </div>
      </section>

      {/* Stats Cards */}
      <div className="users-admin-stats">
        <article className="users-stat users-stat--blue"><div><User /></div><span>Comunidade<strong>{(users || []).length}</strong><small>utilizadores registados</small></span></article>
        <article className="users-stat users-stat--green"><div><Shield /></div><span>Contas ativas<strong>{activeUsers}</strong><small>com acesso à plataforma</small></span></article>
        <article className="users-stat users-stat--violet"><div><Mail /></div><span>Clientes<strong>{clientUsers}</strong><small>à procura de serviços</small></span></article>
      </div>

      {/* Contas pendentes de ativação (contingência SMS) */}
      {!roleFilter && (pending || []).length > 0 && (
        <div className="users-pending-panel">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-amber-400" />
            <h3 className="text-white font-semibold">Contas à espera de ativação ({pending.length})</h3>
          </div>
          <p className="text-gray-400 text-sm mb-3">
            Estas contas registaram-se mas o SMS de verificação não foi entregue. Confirme a identidade e ative para o utilizador poder entrar.
          </p>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.user_id} className="users-pending-row">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium">{u.full_name} <span className="text-gray-500">· {u.role === 'worker' ? 'Profissional' : 'Cliente'}</span></p>
                  <p className="text-gray-400 text-xs">{u.phone_number} · {u.email}</p>
                </div>
                <button
                  onClick={() => handleActivate(u)}
                  disabled={activating === u.user_id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  {activating === u.user_id ? 'A ativar…' : 'Ativar conta'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Users Table */}
      <section className="users-admin-table"><DataTable data={users || []} columns={columns} pageSize={10} emptyMessage="Nenhum utilizador encontrado." /></section>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.open}
        title="Eliminar Utilizador"
        message={`Tem certeza que deseja eliminar o utilizador "${deleteModal.user?.full_name}"? Esta ação não pode ser desfeita.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ open: false, user: null })}
        confirmText="Eliminar"
        type="danger"
      />

      {/* Add User Modal */}
      {addModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="users-create-modal max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">Novo Utilizador</h3>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Nome Completo</label>
                <input
                  type="text"
                  required
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white 
                             focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white 
                             focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Telefone</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white 
                             focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Tipo</label>
                <select
                  value={newUser.user_type}
                  onChange={(e) => setNewUser({ ...newUser, user_type: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white 
                             focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="client">Cliente</option>
                  <option value="worker">Profissional</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setAddModal(false)}
                  className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-teal-500 text-white hover:bg-teal-600 transition-colors"
                >
                  Criar Utilizador
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
